import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy, OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { BehaviorSubject, interval, NEVER, Observable, Observer, Subject, timer } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { SliderItemDirective } from '../../directives/slider-item.directive';
import { SliderItemElementDirective } from '../../directives/slider-item-element.directive';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { I18nService } from '@personal-area/global/i18n';

export interface SliderSettings {
  width?: string;
  showControls?: boolean;
  showDots?: boolean;
  calcHorizontalPad?: boolean;
  slideToShow?: number;
  animate3d?: boolean;
  fullWidth?: boolean;
  resetOnChanges?: boolean;
  infinite?: boolean;
  scale?: string;
  slidesToShow?: number;
  itemsPerSlide?: number;
  horizontalPad?: number;
  autoplayInterval?: number; // seconds
  animationDuration?: number; // seconds
  moveSliderOnSlideClick?: boolean;
}

@Component({
  selector: 'slider',
  exportAs: 'slider', // so one could use public methods from the outside
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  imports: [CommonModule, SliderItemElementDirective, SliderItemDirective, TranslateModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SliderComponent implements OnInit, AfterViewInit, OnDestroy {
  /*
  _itemsElements is used to get access to the HTML element, passed as the slider item (structural directive (_items)
  doesn't have access to it by itself)
  */
  @ViewChildren(SliderItemElementDirective, { read: ElementRef }) private _itemsElements: QueryList<ElementRef>;
  @ViewChildren('dot') private _dotsRef: QueryList<ElementRef>;
  @ViewChild('slider', { static: true }) private _slider: ElementRef;
  @ViewChild('track', { static: true }) private _track: ElementRef;

  private _dots: undefined[];
  get dots(): undefined[] {
    return this._dots;
  }

  /**
   * Settings
   * @param {SliderSettings} config - Config to be set as a slider settings
   * May contain of (each setting is optional):
   - width: Desired slider width. Default is '100%'
   - showControls: Should slider has control btns? Default is true
   - showDots: Should slider has dots? Default is false
   - calcHorizontalPad: whether horizontal pad should be calculated automatically. Default: false
   - slideToShow: Specify the slide position to show as the first one in a row. Default is 0
   - animate3d: Should slider be animated as a 3d carousel (transform: translate, scale)?
   - fullWidth: Should each slider's slide fill the entire slider width?
   - resetOnChanges: Should slider rerender on slides' changes?
   - infinite: Should slider has effect of infinite slides?
   - scale: Is used for enabled 'animate3d' setting. Stands for transform(scale).
   - slidesToShow: How many slides should be visible within one viewport? Default is 3
   - itemsPerSlide: How many items a slide consists of?
   - horizontalPad: Specify the desired horizontal padding for each slider's slide in pixels. Default is 0px
   - autoplayInterval: Specify the desired interval to autoplay slides (seconds)
   - animationDuration: Specify the desired duration of slides' animation (seconds). Default is 0.5s

   * @memberof SliderComponent
   */
  private _settings: SliderSettings = {
    width: '100%',
    showControls: true,
    showDots: false,
    calcHorizontalPad: false,
    animate3d: false,
    slidesToShow: 1,
    slideToShow: 0,
    horizontalPad: 0,
    animationDuration: .5,
    moveSliderOnSlideClick: true
  };

  @Input('settings')
  set settings(config: SliderSettings) {
    this._settings = {
      ...this._settings,
      ...config
    };
  }

  get settings(): SliderSettings {
    return this._settings || {};
  }

  private _contentElementsLength: number;

  private _items: QueryList<SliderItemDirective>;
  @ContentChildren(SliderItemDirective)
  set items(elements: QueryList<SliderItemDirective>) {
    this._items = elements;
    this._contentElementsLength = elements.length;

    if (!this.settings.slidesToShow) {
      throw new Error('Error! No slides to show');
    }
    const padding = this.settings.infinite || this.settings.animate3d ? 0 : this.settings.slidesToShow - 1;

    this._dots = new Array(this._contentElementsLength - padding);
    if (this.settings.infinite) {
      const test = this._items.toArray();
      for (let i = 0; i < this.settings.slidesToShow + 2; i++) {
        test.push(test[i]);
      }
      this._items.reset(test);
    }
  }

  get items(): QueryList<SliderItemDirective> {
    return this._items;
  }

  get contentItemsLength(): number {
    return this._contentElementsLength;
  }

  private _ngUnsubscribe: Subject<void>;
  private _pause: BehaviorSubject<boolean>;

  private _itemWidth: number;
  private _sliderWidth: number;
  private _trackWidth: number;
  private _currentSlide: number;
  private _visibleElems: number[];

  private _transform: number;

  // styles
  public sliderWrapperStyle: { width?: string; transition?: string };
  public sliderItemStyle: { width?: string; marginLeft?: string; marginRight?: string; transition?: string } = {};

  // swipe
  private _touchStartX: number;
  private _touchStartY: number;

  private _isAnimating: boolean;

  // events
  @Output() currentSlide: EventEmitter<any>;

  // listeners
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this._setSliderSizes();
    if (this.settings.animate3d) {
      this._moveTrack();
    }
  }

  constructor(
    private _renderer: Renderer2,
    private _cdRef: ChangeDetectorRef,
    private _i18nService: I18nService,
    private _translateService: TranslateService
  ) {
    this.currentSlide = new EventEmitter();
    this._ngUnsubscribe = new Subject<void>();
    this._pause = new BehaviorSubject<boolean>(false);
    this._transform = 0;

    this.sliderWrapperStyle = {};
  }

  ngOnInit(): void {
    this._translateService.onLangChange
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this.reset());
  }

  ngAfterViewInit(): void {
    this.reset(false);

    if (this.settings.resetOnChanges) {
      this.items.changes
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(() => this.reset());
    }

    if (this.settings.autoplayInterval && this.contentItemsLength > 1) {
      this._renderer.listen(this._slider.nativeElement, 'mouseover',
        () => this._pause.next(true));
      this._renderer.listen(this._slider.nativeElement, 'mouseleave',
        () => this._pause.next(false));

      this._autoplayInterval()
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(() => this._autoplaySlide());
    }
  }

  private _autoplayInterval(): Observable<number> {
    return new Observable((obs: Observer<number>) => {
      let i = 0;
      const ticker = interval(this.settings.autoplayInterval * 1000).pipe(map(() => i++));

      return this._pause
        .pipe(switchMap(paused => paused ? NEVER : ticker))
        .subscribe(val => obs.next(val), err => obs.error(err), () => obs.complete());
    });
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.unsubscribe();
  }

  private _init(isReset = false): void {
    if (!this._itemsElements || !this._itemsElements.length) {
      throw new Error('Error! No slides for slider');
    }

    if (this.settings.autoplayInterval && this.settings.animationDuration > this.settings.autoplayInterval) {
      throw new Error('Error! animationDuration can\'t be more than autoplayInterval');
    }

    if (!isReset) {
      this._setCurrentSlide(this.settings.slideToShow);
    }

    this._setSliderSizes();
    if (this.settings.animate3d) {
      this._moveTrack();
    }

    this._update();

    // do not animate on init
    timer(500)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this._addCssAnimation());
  }

  public next(): void {
    if (this._currentSlide + (this.settings.infinite ? 0 : 1) === this._dots.length || this._isAnimating) {
      return;
    }

    this._setCurrentSlide((this._currentSlide + 1) % this._dots.length);
    this._update(this._checkDirectionInRtl('right'));
  }

  public prev(): void {
    if (this._currentSlide === 0 || this._isAnimating) {
      return;
    }

    this._setCurrentSlide(((this._currentSlide - 1) + this._dots.length) % this._dots.length);
    this._update(this._checkDirectionInRtl('left'));
  }

  private _update(direction?: 'right' | 'left', multiplier?: number): void {
    this._updateClasses();
    this._setStyles(direction, multiplier);

    if (this.settings.showDots && this.items.length > 1) {
      this._setStylesForDots();
    }

    this._cdRef.detectChanges();
  }

  private get _horizontalPad(): number {
    if (this.settings.calcHorizontalPad) {
      const padding = (this._sliderWidth - (this._itemWidth * this.settings.slidesToShow)) / this.settings.slidesToShow;
      return padding > 0 ? padding : this.settings.horizontalPad;
    }
    return this.settings.horizontalPad;
  }

  private _setSliderSizes(): void {
    this._sliderWidth = this._slider.nativeElement.getBoundingClientRect().width;
    this._itemWidth = this.settings.fullWidth ?
      (this._sliderWidth - this._horizontalPad) :
      this._getSlide(this._currentSlide).getBoundingClientRect().width;
    this._trackWidth = (this._itemWidth + Math.abs(this._horizontalPad)) * this._itemsElements.length;

    this.sliderWrapperStyle.width = `${this._trackWidth}px`;

    this.sliderItemStyle.width = `${this._itemWidth}px`;
    this.sliderItemStyle.marginLeft = `${this._horizontalPad / 2}px`;
    this.sliderItemStyle.marginRight = `${this._horizontalPad / 2}px`;
  }

  private _updateClasses(): void {
    this._itemsElements.forEach(elem => this._renderer.removeClass(elem.nativeElement, 'active'));
    if(this._getSlide(this._currentSlide)) {
      this._renderer.addClass(this._getSlide(this._currentSlide), 'active');
    }
  }

  private _getSlide(index: number): HTMLElement {
    return index >= 0 ?
      this._itemsElements.find((item, i) => i === index)?.nativeElement :
      this._itemsElements.find((item, i) => i === (this.items.length + index))?.nativeElement;
  }

  private _getDot(index: number): HTMLElement {
    return this._dotsRef.find((dot, i) => i === index).nativeElement;
  }

  private _getVisibleElements(direction: 'right' | 'left', multiplier: number): number[] {
    if (this._visibleElems && !direction) {
      return this._visibleElems;
    }

    const isInit = this._visibleElems === undefined;

    const arr = this._visibleElems ? [...this._visibleElems] : [];
    direction = this._visibleElems ? direction : null;
    let counter = multiplier;

    switch (direction) {
      case 'right':
        while (counter > 0) {
          counter--;
          arr.shift();
          arr.push(arr[arr.length - 1] + 1);
        }

        if (this.settings.animate3d && arr.length !== this.settings.slidesToShow) {
          arr.unshift(arr[0] - 1);
        }

        break;
      case 'left':
        while (counter > 0) {
          counter--;
          arr.pop();
          arr.unshift(arr[0] - 1);
        }

        if (this.settings.animate3d && arr.length !== this.settings.slidesToShow) {
          arr.push(arr[arr.length - 1] + 1);
        }
        break;
      default: {
        let position = isInit ? this._currentSlide : 0;
        arr.push(position);
        counter = this.settings.animate3d ?
          this.settings.slidesToShow - (1 + multiplier) :
          this.settings.slidesToShow - 1;
        counter = isInit ? counter : counter + 1;

        while (counter > 0) {
          counter--;
          position++;
          arr.push(position);
        }

        if (this.settings.animate3d && arr.length !== this.settings.slidesToShow) {
          arr.unshift(arr[0] - 1);
        }
      }
    }

    return arr;
  }

  private _setStyles(direction: 'right' | 'left', multiplier = 1): void {
    if (this.settings.animate3d) {
      this._visibleElems = this._getVisibleElements(direction, multiplier);
      this._itemsElements.forEach((elem, index) => {
        const isElemVisible = this._visibleElems.includes(index);
        const isElemActive = index === this._currentSlide;
        this._renderer.setStyle(elem.nativeElement, 'transform',
          isElemActive ? 'none' : `scale(${this.settings.scale || '0.8'})`);
        this._renderer.setStyle(elem.nativeElement, 'zIndex', isElemActive ? '2' : '1');
        this._renderer.setStyle(elem.nativeElement, 'opacity', isElemVisible ? '1' : '0');
        this._renderer.setStyle(elem.nativeElement, 'pointer-events', isElemVisible ? 'all' : 'none');
      });
    }

    if (!direction || this._isAnimating) {
      return;
    }

    this._isAnimating = true;
    this._moveTrack(direction, multiplier);

    if (this.settings.infinite && this._currentSlide === 0) {
      this._resetTrack();
    } else {
      setTimeout(() => this._isAnimating = false, this.settings.animationDuration * 1000);
    }
  }

  private _moveTrack(direction?: 'left' | 'right', multiplier = 1): void {
    const step = ((this._itemWidth + this._horizontalPad) / this._trackWidth) * 100;

    switch (direction) {
      case 'left':
        this._transform += step * multiplier;
        break;
      case 'right':
        this._transform -= step * multiplier;
        break;
      default: {
        this._transform = this._getSliderCenter() - step * this._currentSlide;
      }
    }

    this._renderer.setStyle(this._track.nativeElement, 'transform', `translateX(${this._transform}%)`);
  }

  private _resetTrack(): void {
    this._transform = 0;

    // disable animation for smooth infinite scrolling
    setTimeout(() => {
      this.sliderWrapperStyle.transition = 'none';
      this.sliderItemStyle.transition = 'none';
      this._renderer.setStyle(this._track.nativeElement, 'transform', `translateX(${this._transform}%)`);
    }, this.settings.animationDuration * 1000);

    // revert animation
    setTimeout(() => {
      this._currentSlide = 0;
      this._visibleElems = null;
      this._isAnimating = false;
      this.onSlideClick(0);

      this._addCssAnimation();
    }, this.settings.animationDuration * 1000 + 1);
  }

  public onSlideClick(index: number): void {
    if (this._isAnimating || !this.settings.moveSliderOnSlideClick) {
      return;
    }

    this._moveSliderByPosition(index);
  }

  public onDotClick(index: number): void {
    if (this._isAnimating) {
      return;
    }

    let slideToSelect = index;
    if (this._currentSlide >= this._dots.length && index <= this._currentSlide + 1 - this._dots.length) {
      slideToSelect = index + this._currentSlide;
    }
    this._moveSliderByPosition(slideToSelect);
  }

  private _moveSliderByPosition(index: number): void {
    const direction = index > this._currentSlide ? 'right' : index === this._currentSlide ? null : 'left';
    const multiplier = Math.abs(index - this._currentSlide);

    this._setCurrentSlide(index);
    this._update(this._checkDirectionInRtl(direction), multiplier);
  }

  public onTouchStart(evt: TouchEvent): void {
    this._touchStartX = evt.changedTouches[0].clientX;
    this._touchStartY = evt.changedTouches[0].clientY;
  }

  public onTouchEnd(evt: TouchEvent): void {
    if (this.contentItemsLength <= 1 || this._isAnimating) {
      return;
    }

    const xDiff = this._touchStartX - evt.changedTouches[0].clientX;
    const yDiff = this._touchStartY - evt.changedTouches[0].clientY;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0) {
        // left
        this._autoplaySlide('right');
      } else {
        // right
        this._autoplaySlide('left');
      }
    } else {
      if (yDiff > 0) {
        // up
      } else {
        // down
      }
    }

    if (this.settings.autoplayInterval) {
      this._pause.next(false);
    }

  }

  private _setStylesForDots(): void {
    if (!this._dotsRef.length) {
      return;
    }
    this._dotsRef.forEach(elem => this._renderer.removeClass(elem.nativeElement, 'active'));
    this._renderer.addClass(
      this._getDot(this._currentSlide >= this._dots.length ? this._currentSlide - this._dots.length : this._currentSlide),
      'active'
    );
  }

  public reset(notInit = true): void {
    this._transform = 0;
    this._visibleElems = undefined;
    this._setCurrentSlide(0, true);

    // disable animation for smooth reset
    this.sliderWrapperStyle.transition = 'none';
    this._renderer.setStyle(this._track.nativeElement, 'transform', `translateX(${this._transform}%)`);

    // upd view
    this._cdRef.detectChanges();

    // revert animation
    setTimeout(() => this._addCssAnimation(), this.settings.animationDuration * 1000);

    this._init(notInit);
  }

  private _autoplaySlide(direction: 'right' | 'left' = 'right'): void {
    const position = direction === 'right' ? this._currentSlide + 1 : this._currentSlide - 1;

    if ((!this.settings.infinite && position >= this._dots.length) || position < 0) {
      this._pause.next(true);
      return;
    }

    this._setCurrentSlide(position % this._dots.length);

    this._update(this._checkDirectionInRtl(direction));
  }

  private _setCurrentSlide(index = 0, isReset = false): void {
    if (this._isAnimating && !isReset) {
      return;
    }

    const nextSlide = this.items.find((item, i) => i === index);
    if (!nextSlide) {
      return;
    }
    this._currentSlide = index;
    this.currentSlide.next(nextSlide.slideData);
  }

  private _getSliderCenter(): number {
    return (((this._sliderWidth - this._itemWidth - this._horizontalPad) / 2) / this._trackWidth) * 100;
  }

  private _addCssAnimation(): void {
    this.sliderWrapperStyle.transition = `all ${this.settings.animationDuration}s ease`;
    this.sliderItemStyle.transition = `all ${this.settings.animationDuration}s ease 0s`;
  }

  private _checkDirectionInRtl(direction: 'left' | 'right'): 'left' | 'right' {
    if (this._i18nService.isRtl && direction) {
      return direction === 'left' ? 'right' : 'left';
    }

    return direction;
  }
}
